<template>
  <div class="add-bank-card">
    <van-icon name="cross" class="close-icon" @click="emit('close')" />

    <div class="header">
      <div class="title">添加银行卡</div>
      <div class="subtitle">请绑定持卡人本人的银行卡</div>
    </div>

    <div class="form">
      <van-field v-model="formData.cardHolder" label="持卡人" placeholder="持卡人姓名" :border="false" label-width="60px" />

      <van-field v-model="formData.cardNumber" label="卡号" placeholder="持卡人本人银行卡号" :border="false" label-width="60px" />

      <van-field v-model="formData.phone" label="手机号" placeholder="持卡人银行预留手机号" :border="false" label-width="60px" />

      <van-field v-model="authData.smsCode" label="验证码" placeholder="输入验证码" :border="false" label-width="60px">
        <template #button>
          <van-button size="small" type="primary" plain class="verify-btn" @click="getVerificationCode"
            :disabled="counting">
            {{ counting ? `${countdown}s` : '获取验证码' }}
          </van-button>
        </template>
      </van-field>

      <div class="submit-wrapper">
        <van-button type="primary" block class="submit-btn" @click="submitForm">
          绑定银行卡
        </van-button>
      </div>
    </div>

    <div class="bank-list">
      <div class="list-title">支持银行列表</div>
      <div class="list-content">
        <div class="bank-item" v-for="(bank, index) in bankList" :key="index">
          {{ bank.bankName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { reactive, ref, onBeforeUnmount, onMounted } from 'vue'
import { showToast } from 'vant'
import { getBindCardCode, getBankList, bindCard } from '@/api/wallet'

interface FormData {
  cardHolder: string
  cardNumber: string
  phone: string
}

const emit = defineEmits(['close'])

const formData = reactive<FormData>({
  cardHolder: '',
  cardNumber: '',
  phone: '',
})

const authData = ref<any>({
  authSn: '',
  requestNo: '',
  smsCode: ''
})

const bankList = ref<any[]>([])

// 添加倒计时相关状态
const counting = ref(false)
const countdown = ref(60)
let timer: ReturnType<typeof setInterval> | null = null

// 表单校验规则
const validateForm = () => {
  if (!formData.cardHolder) {
    showToast('请输入持卡人姓名')
    return false
  }
  if (!formData.cardNumber) {
    showToast('请输入银行卡号')
    return false
  }
  if (!/^\d{16,19}$/.test(formData.cardNumber)) {
    showToast('请输入正确的银行卡号')
    return false
  }
  if (!formData.phone) {
    showToast('请输入手机号')
    return false
  }
  if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
    showToast('请输入正确的手机号')
    return false
  }
  return true
}

// 获取验证码
const getVerificationCode = async () => {
  if (counting.value) return
  if (!formData.cardNumber) {
    showToast('请输入银行卡号')
    return
  }
  if (!formData.phone) {
    showToast('请输入手机号')
    return
  }
  if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
    showToast('请输入正确的手机号')
    return
  }

  try {
    // TODO: 调用获取验证码接口
    await getBindCardCode({
      bankAccountNo: formData.cardNumber,
      bankMobile: formData.phone
    }).then(res => {
      console.log(res)
      if (res.code === 200) {
        showToast('验证码已发送')
        authData.value.authSn = res.data.authSn
        authData.value.requestNo = res.data.requestNo
        // 开始倒计时
        counting.value = true
        countdown.value = 60
        timer = setInterval(() => {
          countdown.value--
          if (countdown.value <= 0) {
            counting.value = false
            if (timer) {
              clearInterval(timer)
              timer = null
            }
          }
        }, 1000)
      } else {
        showToast(res.msg)
      }
    })
  } catch (error) {
    console.error(error)
  }
}

// 提交表单
const submitForm = async () => {
  if (!validateForm()) return
  if (!authData.value.smsCode) {
    showToast('请输入验证码')
    return
  }

  try {
    // TODO: 调用提交表单接口
    await bindCard(authData.value).then(res => {
      console.log(res)
      if (res.code === 200) {
        showToast('添加银行卡成功')
        emit('close')
      } else {
        showToast(res.msg)
      }
    })
  } catch (error) {
    showToast('添加银行卡失败')
  }
}


// 获取银行卡列表
const getList = async () => {
  const res = await getBankList().then((res) => {
    if (res.code === 200) {
      bankList.value = res.data
    }
  })
}

onMounted(() => {
  getList()
})

// 组件销毁时清除定时器
onBeforeUnmount(() => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
})
</script>

<style lang="scss" scoped>
.add-bank-card {
  background: #fff;
  padding: 60px 32px;
  position: relative;
  color: #333;

  .close-icon {
    position: fixed; // 改为 fixed 定位
    top: 16px;
    left: 16px;
    font-size: 24px;
    cursor: pointer;
    z-index: 999; // 增加 z-index 确保按钮始终显示在最上层
  }

  .header {
    text-align: center;
    margin-bottom: 20px;

    .title {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 7px;
    }

    .subtitle {
      font-size: 14px;
    }
  }

  .form {
    .van-field {
      margin-bottom: 16px;
      display: flex;
      align-items: center;

      .van-field__label {
        color: #333;
        font-weight: 500;
      }

      .van-field__body input {
        color: #333;

        &::placeholder {
          color: #ccc;
        }
      }
    }
  }

  .verify-btn {
    width: 83px;
    height: 30px;
    padding: 0;
    font-size: 13px;
  }

  .submit-wrapper {
    margin-top: 24px;
    display: flex;
    justify-content: center;

    .submit-btn {
      width: 60%;
      height: 44px;
      border-radius: 22px;
      font-size: 16px;
    }
  }

  .bank-list {
    margin-top: 24px;

    .list-title {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 12px;
    }

    .list-content {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 3px;

      .bank-item {
        height: 35px;
        background: #f6f6f6;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #767676;
        border-radius: 5px;
      }
    }
  }
}
</style>
